/**
 * LOOKING GLASS — Immich Archive Panel
 *
 * Browse the remote Immich asset archive and import assets onto the canvas.
 * Structure and inline-style dialect follow BookmarksPanel.jsx (the closest
 * sibling): { isOpen, onClose } + full-viewport scrim + local searchQuery +
 * result grid + an Import action per card.
 *
 * Persistence rules honoured here:
 *  - Remote results are NEVER pushed through the local Fuse.js SearchEngine
 *    (it indexes local canvas items only) — filtering happens server-side
 *    via GET /assets?q=.
 *  - Importing reuses the existing store actions (addImage / addNote), which
 *    own the IndexedDB writes. No new persistence path is introduced.
 *
 * Mobile-UI compliance (this repo uses inline styles, not Tailwind):
 *  T-1  every button/input/select has minWidth+minHeight 44
 *  S-1  top + bottom safe-area insets via env()
 *  S-4  full-viewport panel uses 100dvh, never 100vh
 *  L-1  long filenames truncate (ellipsis) and the grid is minWidth:0 safe
 *  L-3  filter rows and card rows set flexWrap
 *  T-3  no hover-gated controls — every action is an always-visible button
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  MagnifyingGlass,
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  DownloadSimple,
  Image as ImageIcon,
  FilmStrip,
  FilePdf,
  SpeakerHigh,
  Archive as ArchiveIcon,
  CheckCircle,
  SpinnerGap,
  CloudSlash,
} from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore.js';
import {
  previewUrl, assetTitle, formatBytes, formatDate, isImageAsset, cardImageFor,
} from '../store/immichSlice.js';

// ── Local style tokens (camelCase inline objects, per repo convention) ──────
const T1 = { minWidth: 44, minHeight: 44 };

const ACCENT = '#8B5CF6';

const iconForType = (asset) => {
  const mime = asset?.mime || '';
  if (/^video\//i.test(mime)) return FilmStrip;
  if (/^audio\//i.test(mime)) return SpeakerHigh;
  if (/^application\/pdf$/i.test(mime) || /\.pdf$/i.test(asset?.filename || '')) return FilePdf;
  return ImageIcon;
};

function IconButton({ label, onClick, children, disabled, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      style={{
        ...T1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: '44px', height: '44px',
        borderRadius: '8px',
        border: '1px solid var(--color-border)',
        background: 'transparent',
        color: disabled ? 'var(--text-disabled)' : 'var(--text-secondary)',
        cursor: disabled ? 'default' : 'pointer',
        flexShrink: 0,
        ...(style || {}),
      }}
    >
      {children}
    </button>
  );
}

/** Server-driven select. Native <select> + explicit 44px box (T-1). */
function FacetSelect({ value, onChange, label, options, allLabel }) {
  return (
    <select
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        ...T1,
        maxWidth: '100%',
        padding: '8px 10px',
        borderRadius: '10px',
        border: '1px solid var(--color-border)',
        background: 'rgba(255,255,255,0.04)',
        color: 'var(--text-primary)',
        fontFamily: 'var(--font-ui)',
        fontSize: '11px',
        outline: 'none',
        cursor: 'pointer',
      }}
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  );
}

export function ImmichPanel({ isOpen, onClose }) {
  // Local canvas actions — the ONLY persistence path for imported assets.
  const { addImage, addNote, selectItem } = useStore();

  // Immich slice state + actions.
  const {
    immich, initImmich, loadImmichAssets, loadImmichFacets, goToImmichPage,
    setImmichFilters, openImmichAsset, closeImmichAsset, testImmich,
    markImmichImporting, finishImmichImport, isImmichImported,
  } = useStore();

  const {
    assets, total, page, pages, projects, tags, filters,
    loading, error, connection, detail, detailLoading, config,
  } = immich;

  // Local input mirror of the server-side query so typing stays responsive.
  const [queryInput, setQueryInput] = useState(filters.q || '');
  const [showArchived, setShowArchived] = useState(Boolean(filters.isArchived));
  const searchTimer = useRef(null);
  const didInit = useRef(false);

  // Load config + first page when the panel opens.
  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    initImmich();
    loadImmichFacets();
    loadImmichAssets(1);
  }, [isOpen, initImmich, loadImmichFacets, loadImmichAssets]);

  // Debounce the query box into a server-side ?q= search.
  const handleQueryChange = useCallback((value) => {
    setQueryInput(value);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setImmichFilters({ q: value });
    }, 320);
  }, [setImmichFilters]);

  // Cancel a pending debounce if the panel closes mid-typing.
  useEffect(() => () => { if (searchTimer.current) clearTimeout(searchTimer.current); }, []);

  const handleImport = useCallback(async (asset) => {
    if (isImmichImported(asset.id)) {
      toast('Already on the canvas', { icon: 'ℹ️' });
      return;
    }
    markImmichImporting(asset.id);
    try {
      // Preview bytes come from the byte-serving route. preview_path /
      // thumb_path / external_path are absolute local filesystem paths on the
      // API host and are meaningless to a browser, so they are never used as
      // an <img> src.
      const src = cardImageFor(asset);
      const item = await addImage(src);
      if (item) {
        await useStore.getState().updateItem(item.id, {
          content: {
            ...item.content,
            title: assetTitle(asset),
            description: asset.notes || '',
            url: src,
          },
          meta: {
            ...item.meta,
            source: 'immich',
            tags: [],
            immich: {
              id: asset.id,
              type: asset.type || null,
              mime: asset.mime || null,
              size: asset.size ?? null,
              hash: asset.hash || null,
              path: asset.path || null,
              preview: previewUrl(asset),
              modified_at: asset.modified_at || null,
              imported_at: Date.now(),
            },
          },
        });
        // Non-image assets carry no inline preview — leave a pointer note too.
        if (!isImageAsset(asset)) {
          await addNote();
        }
        selectItem(item.id);
        finishImmichImport(asset.id, true);
        toast(`${assetTitle(asset)} added to canvas`, { icon: '✅' });
      } else {
        finishImmichImport(asset.id, false);
        toast('Could not create the canvas item', { icon: '⚠️' });
      }
    } catch (err) {
      finishImmichImport(asset.id, false);
      toast(`Import failed: ${err?.message || err}`, { icon: '⚠️' });
    }
  }, [addImage, addNote, selectItem, isImmichImported, markImmichImporting, finishImmichImport]);

  const handleOpenDetail = useCallback(async (asset) => {
    openImmichAsset(asset.id);
  }, [openImmichAsset]);

  const handleRefresh = useCallback(() => {
    loadImmichFacets();
    loadImmichAssets(page);
  }, [loadImmichAssets, loadImmichFacets, page]);

  if (!isOpen) return null;

  const canPrev = page > 1 && !loading;
  const canNext = page < pages && !loading;

  return (
    <>
      {/* Scrim — sits one step below the panel, matching BookmarksPanel */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          zIndex: 'calc(var(--z-dropdown) - 1)',
          background: 'rgba(0,0,0,0.40)',
          animation: 'ctx-appear 0.15s ease both',
        }}
      />

      <style>{`@keyframes lg-immich-slide { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>

      {/* Panel */}
      <div
        role="dialog"
        aria-label="Immich archive"
        style={{
          position: 'fixed',
          top: 0, left: 0,
          height: '100dvh',                    // S-4: never 100vh
          width: 'min(460px, 100vw)',
          zIndex: 'var(--z-dropdown)',
          background: 'var(--glass-frost)',
          backdropFilter: 'blur(32px) saturate(120%)',
          WebkitBackdropFilter: 'blur(32px) saturate(120%)',
          borderRight: '1px solid var(--color-border)',
          boxShadow: '8px 0 48px var(--glass-cast-shadow)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: 'lg-immich-slide 0.25s cubic-bezier(0.32,0.72,0,1) both',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: '8px',
          padding: '16px 20px',
          paddingTop: 'max(16px, env(safe-area-inset-top))',   // S-1
          borderBottom: '1px solid var(--color-border)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
            <ArchiveIcon size={18} weight="regular" style={{ color: ACCENT, flexShrink: 0 }} />
            <span style={{
              fontFamily: 'var(--font-ui)', fontSize: '13px', fontWeight: 600,
              color: 'var(--text-primary)', letterSpacing: '0.08em',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>IMMICH ARCHIVE</span>
            <span style={{
              fontFamily: 'var(--font-ui)', fontSize: '11px',
              color: 'var(--text-disabled)',
              background: 'rgba(255,255,255,0.06)',
              padding: '2px 8px', borderRadius: '10px',
              flexShrink: 0,
            }}>{total}</span>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
            <IconButton label="Refresh archive" onClick={handleRefresh} disabled={loading}>
              <ArrowClockwise size={16} weight="regular" />
            </IconButton>
            <IconButton label="Close Immich archive" onClick={onClose}>
              <X size={16} weight="regular" />
            </IconButton>
          </div>
        </div>

        {/* ── Connection strip ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
          padding: '8px 20px',
          borderBottom: '1px solid var(--color-border)',
          fontFamily: 'var(--font-ui)', fontSize: '10px',
          color: 'var(--text-disabled)',
          flexShrink: 0,
        }}>
          <span style={{
            minWidth: 0, flex: '1 1 120px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {config?.baseUrl || '/api/immich'}
          </span>
          {connection && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px', flexShrink: 0,
              color: connection.ok ? '#22c55e' : '#ef4444',
            }}>
              {connection.ok
                ? <><CheckCircle size={12} /> connected{connection.total != null ? ` · ${connection.total}` : ''}</>
                : <><CloudSlash size={12} /> {connection.message}</>}
            </span>
          )}
          {!connection && (
            <button
              type="button"
              onClick={testImmich}
              style={{
                ...T1,
                padding: '0 12px',
                borderRadius: '8px',
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)', fontSize: '10px', fontWeight: 600,
                letterSpacing: '0.06em',
                flexShrink: 0,
              }}
            >
              TEST
            </button>
          )}
        </div>

        {/* ── Search + filters (L-3: both rows wrap) ── */}
        <div style={{
          padding: '10px 20px',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex', flexDirection: 'column', gap: '8px',
          flexShrink: 0,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '8px 12px', borderRadius: '10px',
            border: '1px solid var(--color-border)',
            background: 'rgba(255,255,255,0.03)',
          }}>
            <MagnifyingGlass size={14} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} />
            <input
              type="search"
              value={queryInput}
              onChange={(e) => handleQueryChange(e.target.value)}
              placeholder="Search the archive..."
              aria-label="Search the archive"
              style={{
                flex: 1, minWidth: 0, minHeight: '44px',
                border: 'none', background: 'transparent',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-ui)', fontSize: '12px',
                outline: 'none',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <FacetSelect
              label="Filter by project"
              allLabel="All projects"
              options={projects}
              value={filters.project}
              onChange={(v) => setImmichFilters({ project: v })}
            />
            <FacetSelect
              label="Filter by tag"
              allLabel="All tags"
              options={tags}
              value={filters.tag}
              onChange={(v) => setImmichFilters({ tag: v })}
            />
            <button
              type="button"
              onClick={() => { setShowArchived((s) => !s); setImmichFilters({ isArchived: showArchived ? null : true }); }}
              aria-pressed={showArchived}
              style={{
                ...T1,
                padding: '0 12px',
                borderRadius: '10px',
                border: '1px solid var(--color-border)',
                background: showArchived ? 'rgba(139,92,246,0.18)' : 'transparent',
                color: showArchived ? ACCENT : 'var(--text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)', fontSize: '11px',
              }}
            >
              Archived
            </button>
          </div>
        </div>

        {/* ── Asset detail (inline, replaces the grid while open) ── */}
        {detailLoading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <SpinnerGap size={16} className="lg-immich-spin" />
            <span style={{ fontFamily: 'var(--font-ui)', fontSize: '11px', color: 'var(--text-disabled)' }}>Loading asset…</span>
          </div>
        )}

        {!detailLoading && detail && (
          <div style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            padding: '12px 20px',
            paddingBottom: 'calc(12px + env(safe-area-inset-bottom))',
            display: 'flex', flexDirection: 'column', gap: '10px',
          }}>
            <button
              type="button"
              onClick={closeImmichAsset}
              style={{
                ...T1,
                alignSelf: 'flex-start',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '0 12px',
                borderRadius: '10px',
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)', fontSize: '11px',
              }}
            >
              <ArrowLeft size={12} /> BACK TO RESULTS
            </button>

            <div style={{
              borderRadius: '12px',
              border: '1px solid var(--color-border)',
              background: 'rgba(255,255,255,0.03)',
              overflow: 'hidden',
            }}>
              {isImageAsset(detail) ? (
                <img
                  src={previewUrl(detail)}
                  alt={assetTitle(detail)}
                  style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '46dvh', objectFit: 'contain', background: 'rgba(0,0,0,0.35)' }}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '120px' }}>
                  {(() => { const DIcon = iconForType(detail); return <DIcon size={36} style={{ color: 'var(--text-disabled)' }} />; })()}
                </div>
              )}
            </div>

            <div style={{ fontFamily: 'var(--font-ui)', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>
              {assetTitle(detail)}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {[
                { k: 'Type', v: detail.type || '—' },
                { k: 'Size', v: formatBytes(detail.size) },
                { k: 'Dimensions', v: detail.width && detail.height ? `${detail.width}×${detail.height}` : '—' },
                { k: 'Created', v: formatDate(detail.created_at) || '—' },
                { k: 'Modified', v: formatDate(detail.modified_at) || '—' },
                { k: 'Last seen', v: formatDate(detail.last_seen_at) || '—' },
              ].map((row) => (
                <div key={row.k} style={{
                  display: 'flex', gap: '6px', alignItems: 'baseline', flexWrap: 'wrap',
                  padding: '6px 10px', borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  background: 'rgba(255,255,255,0.02)',
                }}>
                  <span style={{ fontFamily: 'var(--font-ui)', fontSize: '10px', color: 'var(--text-disabled)', letterSpacing: '0.08em' }}>{row.k.toUpperCase()}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-secondary)', overflowWrap: 'anywhere' }}>{row.v}</span>
                </div>
              ))}
            </div>

            {detail.notes && (
              <div style={{
                fontFamily: 'var(--font-ui)', fontSize: '11px', color: 'var(--text-secondary)',
                whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                padding: '10px 12px', borderRadius: '10px',
                border: '1px solid var(--color-border)', background: 'rgba(255,255,255,0.02)',
              }}>
                {detail.notes}
              </div>
            )}

            {Array.isArray(detail.tags) && detail.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {detail.tags.map((t) => (
                  <span key={t.id || t.name} style={{
                    ...T1, display: 'inline-flex', alignItems: 'center',
                    padding: '0 10px',
                    borderRadius: '10px',
                    border: `1px solid ${t.color || 'var(--color-border)'}`,
                    fontFamily: 'var(--font-ui)', fontSize: '10px',
                    color: 'var(--text-secondary)',
                  }}>
                    {t.name}
                  </span>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => handleImport(detail)}
              disabled={immich.importingIds.includes(detail.id) || isImmichImported(detail.id)}
              style={{
                ...T1, width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 16px',
                borderRadius: '12px',
                border: 'none',
                background: isImmichImported(detail.id) ? 'rgba(34,197,94,0.16)' : ACCENT,
                color: isImmichImported(detail.id) ? '#22c55e' : '#fff',
                cursor: isImmichImported(detail.id) ? 'default' : 'pointer',
                fontFamily: 'var(--font-ui)', fontSize: '12px', fontWeight: 600,
                letterSpacing: '0.06em',
              }}
            >
              {isImmichImported(detail.id)
                ? <><CheckCircle size={14} /> ON CANVAS</>
                : immich.importingIds.includes(detail.id)
                  ? <><SpinnerGap size={14} /> IMPORTING…</>
                  : <><DownloadSimple size={14} /> IMPORT TO CANVAS</>}
            </button>
          </div>
        )}

        {/* ── Results ── */}
        {!detail && !detailLoading && (
          <div style={{
            flex: 1, overflowY: 'auto', overflowX: 'hidden',
            padding: '10px 12px',
            paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
            display: 'flex', flexDirection: 'column', gap: '6px',
          }}>
            {error && (
              <div style={{
                display: 'flex', alignItems: 'flex-start', gap: '8px',
                padding: '10px 12px', borderRadius: '10px',
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(239,68,68,0.20)',
                fontFamily: 'var(--font-ui)', fontSize: '11px', color: '#ef4444',
                overflowWrap: 'anywhere',
              }}>
                <CloudSlash size={14} style={{ flexShrink: 0, marginTop: '1px' }} />
                <span>{error}</span>
              </div>
            )}

            {!error && loading && assets.length === 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '40px 12px',
              }}>
                <SpinnerGap size={16} className="lg-immich-spin" />
                <span style={{ fontFamily: 'var(--font-ui)', fontSize: '11px', color: 'var(--text-disabled)' }}>Loading archive…</span>
              </div>
            )}

            {!error && !loading && assets.length === 0 && (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                padding: '40px 20px', textAlign: 'center',
              }}>
                <ArchiveIcon size={32} style={{ color: 'var(--text-disabled)', marginBottom: '12px' }} />
                <div style={{ fontFamily: 'var(--font-ui)', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  {filters.q ? 'No matching assets' : 'Archive is empty'}
                </div>
                <div style={{ fontFamily: 'var(--font-ui)', fontSize: '11px', color: 'var(--text-disabled)' }}>
                  {filters.q ? 'Try a different search' : 'Nothing indexed yet, or the API is not mounted at /api/immich'}
                </div>
              </div>
            )}

            {assets.map((asset) => {
              const img = cardImageFor(asset);
              const imported = isImmichImported(asset.id);
              const busy = immich.importingIds.includes(asset.id);
              const TypeIcon = iconForType(asset);
              return (
                <div
                  key={asset.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                    padding: '8px 10px', borderRadius: '12px',
                    border: '1px solid var(--color-border)',
                    background: 'rgba(255,255,255,0.02)',
                  }}
                >
                  {/* Thumbnail — button opens the detail view */}
                  <button
                    type="button"
                    onClick={() => handleOpenDetail(asset)}
                    aria-label={`Details for ${assetTitle(asset)}`}
                    style={{
                      ...T1,
                      width: '48px', height: '48px',
                      borderRadius: '10px',
                      border: '1px solid var(--color-border)',
                      background: 'rgba(0,0,0,0.30)',
                      overflow: 'hidden',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer',
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    {img ? (
                      <img
                        src={img}
                        alt=""
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    ) : (
                      <TypeIcon size={18} style={{ color: 'var(--text-disabled)' }} />
                    )}
                  </button>

                  <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                    <button
                      type="button"
                      onClick={() => handleOpenDetail(asset)}
                      style={{
                        display: 'flex', alignItems: 'center', width: '100%',
                        minHeight: '44px', minWidth: '44px',   // T-1: text-only button still needs a 44px hit area
                        textAlign: 'left',
                        border: 'none', background: 'transparent', padding: 0,
                        cursor: 'pointer',
                        fontFamily: 'var(--font-ui)', fontSize: '12px',
                        color: 'var(--text-primary)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}
                    >
                      {assetTitle(asset)}
                    </button>
                    <div style={{
                      fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-disabled)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {formatBytes(asset.size)} · {formatDate(asset.modified_at) || formatDate(asset.created_at) || '—'}
                      {asset.is_archived ? ' · archived' : ''}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                    <IconButton label={`Import ${assetTitle(asset)} to canvas`} onClick={() => handleImport(asset)} disabled={imported || busy}
                      style={imported ? { color: '#22c55e', borderColor: 'rgba(34,197,94,0.35)' } : undefined}>
                      {imported ? <CheckCircle size={14} /> : busy ? <SpinnerGap size={14} /> : <DownloadSimple size={14} />}
                    </IconButton>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Pagination (S-1: bottom inset) ── */}
        {!detail && !detailLoading && pages > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', flexWrap: 'wrap',
            padding: '10px 16px',
            paddingBottom: 'calc(10px + env(safe-area-inset-bottom))',
            borderTop: '1px solid var(--color-border)',
            flexShrink: 0,
          }}>
            <IconButton label="Previous page" onClick={() => goToImmichPage(page - 1)} disabled={!canPrev}>
              <ArrowLeft size={14} />
            </IconButton>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--text-secondary)', minWidth: '64px', textAlign: 'center' }}>
              {page} / {pages}
            </span>
            <IconButton label="Next page" onClick={() => goToImmichPage(page + 1)} disabled={!canNext}>
              <ArrowRight size={14} />
            </IconButton>
          </div>
        )}
      </div>

      <style>{`
        .lg-immich-spin { animation: lg-immich-spin 0.9s linear infinite; }
        @keyframes lg-immich-spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}